/**
 * Classificador Local para cálculo dos 6 indicadores
 *
 * Por enquanto, usa heurísticas simples baseadas em palavras-chave
 * Futuramente pode ser substituído por um modelo ML leve (transformers.js)
 */

class LocalClassifier {
  constructor() {
    this.ready = false;
    this.loading = false;
    this.error = null;

    // Palavras-chave e padrões para análise
    this.keywords = {
      positive: [
        'sim',
        'ok',
        'perfeito',
        'ótimo',
        'excelente',
        'gostei',
        'interessante',
        'quero',
        'preciso',
        'vou comprar',
        'quanto custa',
        'tenho interesse',
        'me chama',
        'agende',
        'sim, por favor',
      ],
      objection: [
        'não',
        'não tenho interesse',
        'não preciso',
        'caro',
        'muito caro',
        'não tenho dinheiro',
        'depois',
        'pensar',
        'não sei',
        'duvida',
        'preço',
        'valor',
        'custo',
        'muito',
        'difícil',
        'complicado',
      ],
      interest: [
        'produto',
        'serviço',
        'solução',
        'oferta',
        'promoção',
        'desconto',
        'como funciona',
        'explicar',
        'mais informações',
        'detalhes',
        'especificações',
        'benefícios',
        'vantagens',
      ],
      fatigue: [
        'cansado',
        'irritado',
        'não quero mais',
        'pare',
        'para',
        'chega',
        'já falei',
        'repetitivo',
        'chato',
        'aborrecido',
      ],
      conversion: [
        'comprar',
        'vou comprar',
        'fechar negócio',
        'aceitar',
        'contratar',
        'assinar',
        'fechar',
        'confirmar',
        'fazer o pedido',
      ],
      response: [
        '?',
        'o que',
        'qual',
        'como',
        'quando',
        'onde',
        'por que',
        'pergunta',
        'duvida',
        'ajuda',
        'preciso de',
        'você pode',
      ],
    };

    // Pesos para cálculo de probabilidades
    this.weights = {
      positive: 1.0,
      objection: 1.2,
      interest: 1.0,
      fatigue: 0.9,
      conversion: 1.1,
      response: 1.0,
    };
  }

  /**
   * Inicializar classificador (pode carregar modelos futuramente)
   */
  async initialize() {
    try {
      this.loading = true;
      this.error = null;

      // TODO: Carregar modelo ML se disponível (transformers.js)
      // Por enquanto, apenas marcar como pronto

      this.ready = true;
      this.loading = false;

      return true;
    } catch (error) {
      this.error = error.message;
      this.loading = false;
      this.ready = false;
      throw error;
    }
  }

  /**
   * Calcular hash SHA-256 do conteúdo para cache (usando API nativa)
   */
  async calculateHash(content) {
    const encoder = new TextEncoder();
    const data = encoder.encode(content);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Normalizar texto para análise
   */
  normalizeText(text) {
    if (!text || typeof text !== 'string') return '';

    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove acentos
      .trim();
  }

  /**
   * Contar ocorrências de palavras-chave
   */
  countKeywords(normalizedText, keywordList) {
    let count = 0;
    const words = normalizedText.split(/\s+/);

    for (const keyword of keywordList) {
      const normalizedKeyword = this.normalizeText(keyword);

      // Busca exata
      if (normalizedText.includes(normalizedKeyword)) {
        count += 1;
      }

      // Busca por palavra (se palavra única)
      if (normalizedKeyword.split(/\s+/).length === 1) {
        count += words.filter(w => w === normalizedKeyword).length;
      }
    }

    return count;
  }

  /**
   * Calcular probabilidade baseada em palavras-chave
   */
  calculateProbability(normalizedText, keywordList, weight = 1.0) {
    const count = this.countKeywords(normalizedText, keywordList);
    const baseScore = Math.min(
      count / Math.max(keywordList.length / 2, 1),
      1.0
    );

    // Aplicar peso e normalizar para 0-1
    return Math.min(baseScore * weight, 1.0);
  }

  /**
   * Calcular confiança baseada na análise
   */
  calculateConfidence(indicators) {
    // Se nenhum indicador está alto (>0.7), confiança é menor
    const hasHighIndicator = Object.values(indicators).some(v => v > 0.7);

    // Se há muitos indicadores médios, confiança é maior
    const mediumIndicators = Object.values(indicators).filter(
      v => v > 0.4 && v <= 0.7
    ).length;

    // Base de confiança: 0.5 a 0.9
    let confidence = 0.5;

    if (hasHighIndicator) {
      confidence += 0.2;
    }

    if (mediumIndicators >= 3) {
      confidence += 0.2;
    }

    return Math.min(confidence, 0.9);
  }

  /**
   * Classificar mensagem e retornar os 6 indicadores
   *
   * @param {string} messageContent - Conteúdo da mensagem
   * @param {Object} context - Contexto adicional (conversa, histórico, etc)
   * @returns {Promise<Object>} { indicators, confidence, processingTime }
   */
  async classify(messageContent, context = {}) {
    const startTime = performance.now();

    if (!this.ready) {
      await this.initialize();
    }

    if (!messageContent || typeof messageContent !== 'string') {
      return {
        indicators: {
          prob_conversion: 0.5,
          prob_positive_sentiment: 0.5,
          prob_objection: 0.3,
          prob_interest_product_area: 0.5,
          prob_fatigue_annoyance: 0.2,
          prob_response: 0.5,
        },
        confidence: 0.5,
        processingTime: 0,
      };
    }

    const normalizedText = this.normalizeText(messageContent);
    const textLength = normalizedText.length;

    // Calcular indicadores baseados em palavras-chave
    const prob_positive_sentiment = this.calculateProbability(
      normalizedText,
      this.keywords.positive,
      this.weights.positive
    );

    const prob_objection = this.calculateProbability(
      normalizedText,
      this.keywords.objection,
      this.weights.objection
    );

    const prob_interest_product_area = this.calculateProbability(
      normalizedText,
      this.keywords.interest,
      this.weights.interest
    );

    const prob_fatigue_annoyance = this.calculateProbability(
      normalizedText,
      this.keywords.fatigue,
      this.weights.fatigue
    );

    const prob_conversion = this.calculateProbability(
      normalizedText,
      this.keywords.conversion,
      this.weights.conversion
    );

    // Probabilidade de resposta: baseado em perguntas e interrogações
    const hasQuestionMark =
      normalizedText.includes('?') || normalizedText.includes('?');
    const prob_response = hasQuestionMark
      ? 0.8
      : this.calculateProbability(
          normalizedText,
          this.keywords.response,
          this.weights.response
        );

    // Ajustes baseados em comprimento do texto
    // Textos muito curtos tendem a ter menor confiança
    const lengthAdjustment = textLength < 10 ? 0.8 : 1.0;

    const indicators = {
      prob_conversion: Math.min(prob_conversion * lengthAdjustment, 1.0),
      prob_positive_sentiment: Math.min(
        prob_positive_sentiment * lengthAdjustment,
        1.0
      ),
      prob_objection: Math.min(prob_objection * lengthAdjustment, 1.0),
      prob_interest_product_area: Math.min(
        prob_interest_product_area * lengthAdjustment,
        1.0
      ),
      prob_fatigue_annoyance: Math.min(
        prob_fatigue_annoyance * lengthAdjustment,
        1.0
      ),
      prob_response: Math.min(prob_response * lengthAdjustment, 1.0),
    };

    // Calcular confiança
    const confidence = this.calculateConfidence(indicators);

    const processingTime = performance.now() - startTime;

    return {
      indicators,
      confidence,
      processingTime: Math.round(processingTime), // em milissegundos
    };
  }

  /**
   * Verificar se precisa fazer fallback para API
   * Retorna true se: processingTime > 1500ms OU confidence < 0.75
   */
  shouldFallbackToAPI(classificationResult) {
    return (
      classificationResult.processingTime > 1500 ||
      classificationResult.confidence < 0.75
    );
  }
}

// Exportar instância singleton
export default new LocalClassifier();
